import { useQuery } from "@tanstack/react-query";
import { AuditService } from "@/lib/api_client";
import { getAuthHeaders } from "@/lib/api";

export interface PatientImpersonator {
  authUserId: string;
  adminId: string;
  adminName: string;
  lastViewedAt: string;
}

// All doctors who have viewed a specific patient, one entry per doctor (latest visit)
export const usePatientImpersonators = (patientUserId: string | undefined) =>
  useQuery({
    queryKey: ["patient-impersonators", patientUserId],
    queryFn: async () => {
      const res = await AuditService.getApiAuditLogsImpersonatorsByPatientUserId({
        path: { patientUserId: patientUserId! },
        headers: getAuthHeaders(),
      });
      return (res.data ?? []) as PatientImpersonator[];
    },
    enabled: !!patientUserId,
    // Five seconds is deliberate here: this drives the toast that tells a
    // doctor another doctor has just opened the same patient, and it costs one
    // indexed lookup for one patient.
    refetchInterval: 5000,
  });

// Latest viewer per patient, for all patients — used by the list page
export const useAllPatientImpersonators = () =>
  useQuery({
    queryKey: ["all-patient-impersonators"],
    queryFn: async () => {
      const res = await AuditService.getApiAuditLogsImpersonators({
        headers: getAuthHeaders(),
      });
      const rows = (res.data ?? []) as PatientImpersonator[];
      return new Map(rows.map((r) => [r.authUserId, r]));
    },
    // Not five seconds, unlike the per-patient hook above: this feeds a
    // "who last looked, how long ago" column whose finest unit is a minute,
    // and every open Kundebehandling tab re-ran the whole-roster query twelve
    // times a minute to keep it.
    refetchInterval: 60_000,
  });
